import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Phone, MessageSquare, Filter, CheckCircle, BadgeCheck, Loader2, Store, Plus, X, Mail, Building2, ArrowLeft } from 'lucide-react';
import api, { suppliersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Supplier {
  id: string;
  name: string;
  categories: string[];
  rating: number;
  reviews: number;
  distance: string;
  zone: string;
  description: string;
  hourlyRate: string;
  phone: string;
  verified: boolean;
  superProvider: boolean;
  available: boolean;
  image?: string;
}

const norm = (v: string) => (v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export default function SupplierDirectory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<any[]>([{ id: 'all', name: 'Todos', icon: '\ud83d\udd0d' }]);
  const loggedRef = useRef<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'rating' | 'distance' | 'reviews'>('rating');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // F94: Register supplier modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [supplierError, setSupplierError] = useState('');
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    category: 'Plomería',
    phone: '',
    email: '',
    address: '',
    description: '',
  });
  // F95: Supplier detail view state
  const [detailSupplier, setDetailSupplier] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const requestService = async (businessId: string) => {
    setActionBusy(true); setActionMsg('');
    try { await api.post(`/suppliers/directory/${businessId}/request`, {}); setActionMsg('Solicitud enviada — el proveedor te contactará'); }
    catch (x: any) { setActionMsg(x.response?.data?.error || 'No se pudo enviar la solicitud.'); }
    finally { setActionBusy(false); }
  };
  const claimBusiness = async (businessId: string) => {
    setActionBusy(true); setActionMsg('');
    try { await api.post('/suppliers/claim', { business_id: businessId }); setActionMsg('Reclamo enviado. Queda pendiente de aprobación.'); }
    catch (x: any) { setActionMsg(x.response?.data?.error || 'No se pudo enviar el reclamo.'); }
    finally { setActionBusy(false); }
  };

  // F94: Determine if current user is admin for "Agregar Proveedor" button
  const isAdminRole = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const role = (u.role || u.condoRole || '').toLowerCase();
      const roles = Array.isArray(u.roles) ? u.roles : [];
      return role === 'admin' || role === 'super_admin' ||
             roles.some((r: any) => {
               const rl = (typeof r === 'string' ? r : r?.role || '').toLowerCase();
               return rl === 'admin' || rl === 'super_admin';
             });
    } catch { return false; }
  })();

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const condoId = user?.condoId;
      const merged: any[] = [];
      if (condoId) {
        try {
          const res = await suppliersAPI.getAll(condoId);
          const data = res.data?.data ?? res.data;
          const suppliersList = Array.isArray(data) ? data : data?.suppliers || [];
          if (Array.isArray(suppliersList)) merged.push(...suppliersList.map((sp: any) => ({
            ...sp,
            id: sp.id || sp.supplier_id,
            name: sp.name || sp.supplier_name || sp.business_name,
            categories: Array.isArray(sp.categories) ? sp.categories : (typeof sp.categories === 'string' ? sp.categories.split(',').map((c: string) => c.trim()).filter(Boolean) : []),
            rating: typeof sp.rating === 'number' ? sp.rating : (parseFloat(sp.avg_rating) || 0),
            reviews: sp.reviews ?? (parseInt(sp.total_ratings, 10) || 0),
            phone: sp.phone || '', email: sp.email || '',
          })));
        } catch { /* legacy condo supplier list may be empty */ }
      }
      try {
        const mres = await suppliersAPI.getMarketplaceDirectory();
        const mdata = mres.data?.data ?? mres.data;
        const businesses = mdata?.businesses || [];
        merged.push(...businesses.map((b: any) => ({
          id: b.id, name: b.name, categories: [b.category].filter(Boolean),
          rating: 0, reviews: 0, distance: '', zone: b.city || '', description: b.description || '',
          hourlyRate: '', phone: b.phone || '', email: b.email || '', website: b.website || '',
          verified: !!b.claimed, claimed: !!b.claimed, superProvider: false, available: true, __marketplace: true,
        })));
      } catch { /* marketplace directory unavailable */ }
      setSuppliers(merged);
    } catch (err: any) {
      // If endpoint doesn't exist yet, show empty state
      if (err?.response?.status !== 401) {
        console.error('Error loading suppliers:', err);
      }
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [user?.condoId]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  useEffect(() => {
    api.get('/suppliers/categories').then(r => {
      const list = r.data?.data?.categories || [];
      setCategories([{ id: 'all', name: 'Todos', icon: '🔍' }, ...list.map((c: any) => ({ id: c.slug, name: c.label, icon: c.icon || '🧰' }))]);
    }).catch(() => {});
  }, []);

  const filteredSuppliers = suppliers
    .filter(s => {
      const matchesSearch = (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (s.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const sel = categories.find(c => c.id === selectedCategory);
      const matchesCategory = selectedCategory === 'all' || (s.categories || []).some((c: string) => norm(c) === norm(sel?.name || '')) || (s.categories || []).includes(selectedCategory);
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'reviews') return (b.reviews || 0) - (a.reviews || 0);
      return 0;
    });

  // Demand signal: log searches returning zero results (feeds auto-seed category expansion)
  useEffect(() => {
    const q = searchTerm.trim();
    if (q.length < 3 || loading) return;
    const t = setTimeout(() => {
      if (filteredSuppliers.length === 0 && !loggedRef.current.has(norm(q))) {
        loggedRef.current.add(norm(q));
        api.post('/suppliers/search-log', { query: q, results_count: 0 }).catch(() => {});
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filteredSuppliers.length, loading]);

  // F94: Handle supplier registration form submission
  const handleRegisterSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    setSupplierError('');
    setSavingSupplier(true);
    try {
      const condoId = user?.condoId;
      if (!condoId) {
        setSupplierError('Sin condominio asignado.');
        setSavingSupplier(false);
        return;
      }
      await suppliersAPI.registerSupplier(condoId, {
        name: supplierForm.name,
        category: supplierForm.category,
        phone: supplierForm.phone || undefined,
        email: supplierForm.email || undefined,
        address: supplierForm.address || undefined,
        description: supplierForm.description || undefined,
      });
      setSupplierForm({ name: '', category: 'Plomería', phone: '', email: '', address: '', description: '' });
      setShowAddModal(false);
      loadSuppliers();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al registrar proveedor';
      setSupplierError(typeof msg === 'string' ? msg : 'Error al registrar proveedor');
    } finally {
      setSavingSupplier(false);
    }
  };

  // F95: Navigate to full detail page
  const handleNavigateToDetail = (supplierId: string) => {
    navigate(`/services/suppliers/${supplierId}`);
  };

  // F95: Load supplier detail from API (for quick modal preview)
  const handleViewDetail = async (supplierId: string) => {
    setDetailLoading(true);
    setDetailError('');
    setDetailSupplier(null);
    try {
      const condoId = user?.condoId;
      if (!condoId) {
        setDetailError('Sin condominio asignado.');
        setDetailLoading(false);
        return;
      }
      const res = await suppliersAPI.getDetail(condoId, supplierId);
      const data = res?.data?.data ?? res?.data ?? res;
      setDetailSupplier(data);
    } catch (err: any) { const fb = suppliers.find((x: any) => x.id === supplierId); if (fb) { setDetailSupplier(fb); setDetailLoading(false); return; }
      const msg = err?.response?.data?.error || 'Error al cargar detalles';
      setDetailError(typeof msg === 'string' ? msg : 'Error al cargar detalles');
    } finally {
      setDetailLoading(false);
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-1">
      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
      <span className="text-[var(--v-text-primary)] font-medium">{(rating || 0).toFixed(1)}</span>
      <span className="text-[var(--v-text-tertiary)] text-sm">({rating || 0})</span>
    </div>
  );

  const renderSupplierCard = (supplier: Supplier) => (
    <div 
      key={supplier.id}
      onClick={() => handleViewDetail(supplier.id)}
      className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)] hover:border-[var(--v-teal-400)]/50 cursor-pointer transition w-full"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Avatar — smaller on mobile for 320px screens */}
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[var(--v-teal-400)] to-purple-600 flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
          {(supplier.name || '?').charAt(0)}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-white font-semibold truncate">{supplier.name}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {renderStars(supplier.rating)}
                {supplier.verified && (
                  <span className="text-emerald-400 text-xs flex items-center gap-0.5">
                    <CheckCircle className="w-3 h-3" />
                    Verificado
                  </span>
                )}
                {supplier.superProvider && (
                  <span className="bg-amber-500/10 text-amber-400 text-xs px-2 py-0.5 rounded-full">
                    ⭐ Super
                  </span>
                )}
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
              supplier.available 
                ? 'bg-emerald-500/10 text-emerald-400' 
                : 'bg-red-500/10 text-red-400'
            }`}>
              {supplier.available ? 'Disponible' : 'Ocupado'}
            </span>
          </div>

          <p className="text-[var(--v-text-secondary)] text-sm mt-2 line-clamp-2">{supplier.description}</p>

          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
            {supplier.distance && (
              <span className="text-[var(--v-text-secondary)] flex items-center gap-1">
                <MapPin className="w-4 h-4 text-[var(--v-teal-400)]" />
                {supplier.distance}
              </span>
            )}
            {supplier.hourlyRate && (
              <span className="text-emerald-400 font-medium">{supplier.hourlyRate}</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            {(supplier.categories || []).map(catId => {
              const cat = categories.find(c => c.id === catId);
              return cat ? (
                <span key={catId} className="text-xs bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] px-2 py-1 rounded">
                  {cat.icon} {cat.name}
                </span>
              ) : null;
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSupplierDetail = (supplier: Supplier) => (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-4">
        <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] px-4 py-3 flex items-center justify-between rounded-t-2xl z-10">
            <button 
              onClick={() => setSelectedSupplier(null)}
              className="text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]"
            >
              ← Volver
            </button>
            <h2 className="text-[var(--v-text-primary)] font-semibold">Perfil del Proveedor</h2>
            <div className="w-8" />
          </div>

          {/* Profile Content */}
          <div className="p-4 space-y-6">
            {/* Hero */}
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[var(--v-teal-400)] to-purple-600 flex items-center justify-center text-white text-3xl font-bold mx-auto mb-4">
                {(supplier.name || '?').charAt(0)}
              </div>
              <h1 className="text-2xl font-bold text-[var(--v-text-primary)]">{supplier.name}</h1>
              <div className="flex items-center justify-center gap-2 mt-2">
                {renderStars(supplier.rating)}
                {supplier.verified && (
                  <BadgeCheck className="w-5 h-5 text-[var(--v-teal-400)]" />
                )}
              </div>
              {supplier.superProvider && (
                <span className="inline-block bg-amber-500/10 text-amber-400 text-sm px-3 py-1 rounded-full mt-2">
                  ⭐ Super Proveedor
                </span>
              )}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <a 
                href={`https://wa.me/${(supplier.phone || '').replace(/\D/g, '')}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-5 h-5" />
                WhatsApp
              </a>
              <a 
                href={`tel:${supplier.phone}`}
                className="bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Llamar
              </a>
            </div>

            {/* Info */}
            <div className="bg-[var(--v-surface-2)] rounded-xl border border-[var(--v-border-light)] overflow-hidden">
              <div className="p-4 border-b border-[var(--v-border-light)]">
                <h3 className="font-semibold text-white">Información</h3>
              </div>
              <div className="divide-y divide-[var(--v-border-light)]">
                {supplier.hourlyRate && (
                  <div className="p-4 flex justify-between">
                    <span className="text-[var(--v-text-secondary)]">Tarifa</span>
                    <span className="text-emerald-400 font-medium">{supplier.hourlyRate}</span>
                  </div>
                )}
                {supplier.distance && (
                  <div className="p-4 flex justify-between">
                    <span className="text-[var(--v-text-secondary)]">Distancia</span>
                    <span className="text-[var(--v-text-primary)]">{supplier.distance} • {supplier.zone || ''}</span>
                  </div>
                )}
                <div className="p-4 flex justify-between">
                  <span className="text-[var(--v-text-secondary)]">Reseñas</span>
                  <span className="text-[var(--v-text-primary)]">{supplier.reviews || 0} reseñas</span>
                </div>
                {supplier.description && (
                  <div className="p-4">
                    <span className="text-[var(--v-text-secondary)] block mb-2">Descripción</span>
                    <p className="text-[var(--v-text-primary)]">{supplier.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Services */}
            {(supplier.categories || []).length > 0 && (
              <div className="bg-[var(--v-surface-2)] p-4 rounded-xl border border-[var(--v-border-light)]">
                <h3 className="font-semibold text-white mb-3">Servicios</h3>
                <div className="flex flex-wrap gap-2">
                  {supplier.categories.map(catId => {
                    const cat = categories.find(c => c.id === catId);
                    return cat ? (
                      <span key={catId} className="bg-[var(--v-surface-3)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg flex items-center gap-2">
                        {cat.icon} {cat.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 min-w-0 overflow-x-hidden">
      {/* F94: Add Supplier button — admin only */}
      {isAdminRole && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white rounded-lg font-medium transition-all"
          >
            <Plus className="w-5 h-5" />
            Agregar Proveedor
          </button>
        </div>
      )}

      {/* Search Header */}
      <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 w-5 h-5 text-[var(--v-text-tertiary)]" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar plomero, electricista, jardinero..."
            className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] pl-10 pr-4 py-3 rounded-lg border border-[var(--v-border-light)] focus:border-[var(--v-teal-400)] focus:outline-none placeholder-[var(--v-text-tertiary)]"
          />
        </div>

        {/* Category Pills — fixed horizontal overflow */}
        <div className="max-w-full overflow-x-auto pb-2 scrollbar-hide -webkit-overflow-scrolling-touch">
          <div className="flex gap-2 px-1 min-w-0">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? 'bg-[var(--v-teal-400)] text-white'
                    : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-3)]'
                }`}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters & Sort */}
      {!loading && suppliers.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[var(--v-text-secondary)] text-sm">
            {filteredSuppliers.length} proveedores encontrados
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 px-3 py-2 bg-[var(--v-surface-1)] text-[var(--v-text-secondary)] rounded-lg text-sm hover:bg-[var(--v-surface-2)] border border-[var(--v-border-light)]"
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="bg-[var(--v-surface-1)] text-[var(--v-text-secondary)] px-3 py-2 rounded-lg text-sm border border-[var(--v-border-light)] focus:outline-none"
            >
              <option value="rating">Mejor calificación</option>
              <option value="distance">Más cercano</option>
              <option value="reviews">Más reseñas</option>
            </select>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-[var(--v-text-secondary)]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando directorio...
        </div>
      )}

      {/* Empty State */}
      {!loading && suppliers.length === 0 && (
        <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-border-light)] text-center">
          <Store className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-white mb-2">No hay proveedores disponibles</h3>
          <p className="text-[var(--v-text-secondary)]">
            Aún no hay proveedores registrados en tu condominio. Vuelve más tarde.
          </p>
        </div>
      )}

      {/* Results — responsive grid with flex-wrap */}
      {!loading && filteredSuppliers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredSuppliers.map(renderSupplierCard)}
        </div>
      )}

      {!loading && filteredSuppliers.length === 0 && suppliers.length > 0 && (
        <div className="text-center py-12 text-[var(--v-text-tertiary)]">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No se encontraron proveedores</p>
          <p className="text-sm">Intenta con otros filtros</p>
        </div>
      )}

      {/* Supplier Detail Modal (existing) */}
      {selectedSupplier && renderSupplierDetail(selectedSupplier)}

      {/* F94: Register Supplier Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="text-lg font-semibold text-white">Agregar Proveedor</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRegisterSupplier} className="p-6 space-y-4">
              {supplierError && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-sm rounded-lg px-4 py-3">
                  {supplierError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Nombre *</label>
                <input
                  type="text"
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] focus:border-transparent"
                  placeholder="Nombre del proveedor"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Categoría *</label>
                <select
                  value={supplierForm.category}
                  onChange={(e) => setSupplierForm({ ...supplierForm, category: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] focus:border-transparent"
                  required
                >
                  <option value="Plomería">Plomería</option>
                  <option value="Electricidad">Electricidad</option>
                  <option value="Jardinería">Jardinería</option>
                  <option value="Limpieza">Limpieza</option>
                  <option value="Seguridad">Seguridad</option>
                  <option value="Otros">Otros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] focus:border-transparent"
                  placeholder="5551234567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Email</label>
                <input
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] focus:border-transparent"
                  placeholder="proveedor@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Dirección</label>
                <input
                  type="text"
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] focus:border-transparent"
                  placeholder="Calle, número, colonia"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Descripción</label>
                <textarea
                  value={supplierForm.description}
                  onChange={(e) => setSupplierForm({ ...supplierForm, description: e.target.value })}
                  rows={3}
                  className="w-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] focus:border-transparent resize-none"
                  placeholder="Servicios que ofrece..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-secondary)] rounded-lg font-medium hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingSupplier}
                  className="flex-1 px-4 py-2.5 bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {savingSupplier ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* F95: Supplier Detail Modal (from API) */}
      {(detailLoading || detailSupplier || detailError) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {detailLoading ? (
              <div className="flex items-center justify-center py-16 text-[var(--v-text-secondary)]">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Cargando detalles...
              </div>
            ) : detailError ? (
              <div className="p-6 text-center">
                <p className="text-red-500 mb-4">{detailError}</p>
                <button
                  onClick={() => { setDetailError(''); setDetailLoading(false); }}
                  className="px-4 py-2.5 bg-[var(--v-teal-400)] text-white rounded-lg font-medium"
                >
                  Cerrar
                </button>
              </div>
            ) : detailSupplier ? (
              <>
                <div className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                  <h3 className="text-lg font-semibold text-white">Detalle del Proveedor</h3>
                  <button
                    onClick={() => setDetailSupplier(null)}
                    className="p-2 text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  {/* Name & category */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--v-teal-400)] to-purple-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      {(detailSupplier.name || detailSupplier.business_name || '?').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-xl font-bold text-[var(--v-text-primary)]">{detailSupplier.name || detailSupplier.business_name || 'N/A'}</h2>
                      {detailSupplier.category && (
                        <span className="inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]">
                          {detailSupplier.category}
                        </span>
                      )}
                      {detailSupplier.rating && (
                        <div className="flex items-center gap-1 mt-2">
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          <span className="text-[var(--v-text-primary)] font-medium">{(detailSupplier.rating || 0).toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Contact info */}
                  <div className="bg-[var(--v-surface-2)] rounded-xl border border-[var(--v-border-light)] divide-y divide-[var(--v-border-light)]">
                    {detailSupplier.phone && (
                      <div className="p-4 flex items-center gap-3">
                        <Phone className="w-5 h-5 text-[var(--v-teal-400)]" />
                        <a href={`tel:${detailSupplier.phone}`} className="text-[var(--v-text-primary)] hover:text-[var(--v-teal-400)]">{detailSupplier.phone}</a>
                      </div>
                    )}
                    {detailSupplier.email && (
                      <div className="p-4 flex items-center gap-3">
                        <Mail className="w-5 h-5 text-[var(--v-teal-400)]" />
                        <a href={`mailto:${detailSupplier.email}`} className="text-[var(--v-text-primary)] hover:text-[var(--v-teal-400)] break-all">{detailSupplier.email}</a>
                      </div>
                    )}
                    {detailSupplier.address && (
                      <div className="p-4 flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-[var(--v-teal-400)]" />
                        <span className="text-[var(--v-text-primary)]">{detailSupplier.address}</span>
                      </div>
                    )}
                  </div>
                  {/* Description */}
                  {detailSupplier.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--v-text-secondary)] mb-2">Descripción</h4>
                      <p className="text-[var(--v-text-primary)]">{detailSupplier.description}</p>
                    </div>
                  )}
                  {/* Services if available */}
                  {Array.isArray(detailSupplier.services) && detailSupplier.services.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--v-text-secondary)] mb-2">Servicios</h4>
                      <div className="flex flex-wrap gap-2">
                        {detailSupplier.services.map((svc: any, i: number) => (
                          <span key={i} className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-3 py-2 rounded-lg text-sm">
                            {typeof svc === 'string' ? svc : svc?.name || svc?.description || 'Servicio'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-6 border-t border-[var(--v-border-light)] bg-[var(--v-surface-2)] flex justify-end gap-3">
                  {actionMsg && <p className="w-full text-left text-sm text-[var(--v-text-secondary)]">{actionMsg}</p>}
                  <button
                    disabled={actionBusy}
                    onClick={() => requestService(detailSupplier.id)}
                    className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Solicitar servicio
                  </button>
                  {!detailSupplier.claimed && (
                    <button
                      disabled={actionBusy}
                      onClick={() => claimBusiness(detailSupplier.id)}
                      className="px-4 py-2.5 border border-[var(--v-teal-400)] text-[var(--v-teal-400)] rounded-lg font-medium hover:bg-[var(--v-surface-1)] transition-colors disabled:opacity-50"
                    >
                      Reclamar negocio
                    </button>
                  )}
                  {!detailSupplier.__marketplace && (
                  <button
                    onClick={() => { setDetailSupplier(null); navigate(`/services/suppliers/${detailSupplier.id}`); }}
                    className="px-4 py-2.5 bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white rounded-lg font-medium transition-colors"
                  >
                    Ver Detalle Completo
                  </button>
                  )}
                  <button
                    onClick={() => setDetailSupplier(null)}
                    className="px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-secondary)] rounded-lg font-medium hover:bg-[var(--v-surface-1)] transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
